import { ProjectRenderer } from '../ProjectRenderer';
import { Object3D } from 'three';
import { CreatorAccessory } from '@/types/creator';
import { AccessoryMountingSide } from '@/api/ProductTypeVariationApi';
import { CollidableObject } from './CollidableObject';
import { LedTapeConfig } from '@/api/AccessoryApi';
import RENDERER_CONFIG from '@/configs/rendererConfig';

export class LedTape extends CollidableObject {
	static readonly MOUTING_Z_OFFSET = RENDERER_CONFIG.LED_TAPES.MOUTING_Z_OFFSET;

	ledConfig: LedTapeConfig;

	private creatorAccessory: CreatorAccessory;
	private _mountingSide: CreatorAccessory['mountingSide'] =
		AccessoryMountingSide.BACK;
	private _length: number = 1;
	private initialLength: number;

	constructor(
		renderer: ProjectRenderer,
		creatorAccessory: CreatorAccessory,
		object: Object3D,
		initialLength?: number
	) {
		super(
			renderer,
			{
				id: creatorAccessory.refId,
				posY: creatorAccessory.posY,
				linkedObjectId: null,
			},
			object
		);

		this.id = crypto.randomUUID();
		this.ledConfig = structuredClone(
			creatorAccessory.variationAccessory.accessory.ledTapeConfig
		);
		for (const key in this.ledConfig) {
			this.ledConfig[key as keyof LedTapeConfig] /= 1000;
		}

		this.initialLength = initialLength ?? this.dimensions.y;
		this._length = this.dimensions.y;

		this.creatorAccessory = creatorAccessory;

		this.mountingSide = creatorAccessory.mountingSide;
		this.renderer.project.add(object);
	}

	get length() {
		return this._length;
	}

	set length(value: number) {
		const scale = value / this.initialLength;

		this._length = value;
		this.object.scale.set(1, scale, 1);
	}

	get mountingSide() {
		return this._mountingSide;
	}

	set mountingSide(value: CreatorAccessory['mountingSide']) {
		this._mountingSide = value;

		switch (value) {
			case AccessoryMountingSide.FRONT: {
				this.object.rotation.set(0, 0, 0);
				const posZ =
					this.renderer.handleManager.objectsAvailableSpaceBox3.max.z -
					this.dimensions.z / 2 +
					LedTape.MOUTING_Z_OFFSET;
				this.object.position.setZ(posZ);
				break;
			}
			case AccessoryMountingSide.BACK: {
				this.object.rotation.set(0, Math.PI, 0);
				const posZ =
					this.renderer.handleManager.objectsAvailableSpaceBox3.min.z +
					this.dimensions.z / 2 -
					LedTape.MOUTING_Z_OFFSET;
				this.object.position.setZ(posZ);
				break;
			}
		}

		this.updateBox3();
	}

	override set posY(value: number) {
		this._posY = value;
		this.object.position.setY(value);
		this.updateBox3();
	}

	override get posY() {
		return this._posY;
	}

	override updateBox3() {
		this.object.updateMatrixWorld();
		this.collisionBox.setFromObject(this.object, true);
	}

	clone() {
		return new LedTape(
			this.renderer,
			this.creatorAccessory,
			this.object.clone(),
			this.initialLength
		);
	}
}
